import { Grid, Text, Title, Center } from "@mantine/core";
import { DatePickerInput, TimePicker } from "@mantine/dates";
import { IconCalendar } from "@tabler/icons-react";
import { DEFAULT_ICON_SIZE } from "octalog-base";
import { useRef } from "react";

import { LocalDateInternal } from "../../../../../common/calendar/LocalDateInternal";
import { nameOf } from "../../../../../common/Utils";
import type { OrderCreateDto } from "../../../../../generated/client/octalog-service";
import type { OrderFormData } from "../contexts/FormContexts";
import { useDeliveryForm } from "../contexts/FormContexts";
import { useTimePicker } from "../hooks/useTimePicker";

export function DeliveryDetailsSection() {
  const { form, setDateRange } = useDeliveryForm();

  // Refs to access raw inputs for partial time normalization
  const startHoursRef = useRef<HTMLInputElement | null>(null);
  const startMinutesRef = useRef<HTMLInputElement | null>(null);
  const endHoursRef = useRef<HTMLInputElement | null>(null);
  const endMinutesRef = useRef<HTMLInputElement | null>(null);

  // Custom hooks for time picker functionality
  const startTimePicker = useTimePicker({
    fieldName: "deliveryTargetTimeStart",
    hoursRef: startHoursRef,
    minutesRef: startMinutesRef,
    form,
  });

  const endTimePicker = useTimePicker({
    fieldName: "deliveryTargetTimeEnd",
    hoursRef: endHoursRef,
    minutesRef: endMinutesRef,
    form,
  });

  return (
    <Grid ta="left">
      <Grid.Col span={12}>
        <Center>
          <Title c="dimmed" mt="xs" order={4}>
            Lieferzeitraum
          </Title>
        </Center>
      </Grid.Col>
      <Grid.Col span={12}>
        <Title order={6}>Datum</Title>
        <Text c="dimmed" size="xs">
          Genaues Datum oder Datumsbereich
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <DatePickerInput
          {...form.getInputProps(`${nameOf<OrderCreateDto>().deliveryTargetDateStart}`)}
          data-testid="order-form-delivery-date-start"
          label="Datum / Von"
          leftSection={<IconCalendar size={DEFAULT_ICON_SIZE} />}
          onChange={(value) => value && setDateRange(LocalDateInternal.fromString(value))}
          required
          styles={{ input: { textAlign: "center" } }}
          value={form.values.deliveryTargetDateStart?.toDate() ?? null}
          valueFormat="DD.MM.YYYY"
        />
      </Grid.Col>
      <Grid.Col span={6}>
        <DatePickerInput
          {...form.getInputProps(`${nameOf<OrderFormData>().deliveryTargetDateEnd}`)}
          clearable
          data-testid="order-form-delivery-date-end"
          defaultDate={form.values.deliveryTargetDateStart?.toDate()}
          disabled={!form.values.deliveryTargetDateStart}
          label="Bis"
          leftSection={<IconCalendar size={DEFAULT_ICON_SIZE} />}
          minDate={form.values.deliveryTargetDateStart?.toDate()}
          onChange={(value) =>
            setDateRange(form.values.deliveryTargetDateStart!, value ? LocalDateInternal.fromString(value) : undefined)
          }
          styles={{ input: { textAlign: "center" } }}
          value={form.values.deliveryTargetDateEnd?.toDate() ?? null}
          valueFormat="DD.MM.YYYY"
        />
      </Grid.Col>
      <Grid.Col span={12}>
        <Title order={6}>Uhrzeit</Title>
        <Text c="dimmed" size="xs">
          Genaue Uhrzeit, Zeitraum oder keine Angabe (den ganzen Tag)
        </Text>
      </Grid.Col>
      <Grid.Col span={6}>
        <TimePicker
          {...form.getInputProps(`${nameOf<OrderCreateDto>().deliveryTargetTimeStart}`)}
          clearable
          data-testid="order-form-delivery-time-start"
          hoursRef={startHoursRef}
          label="Uhrzeit / Von"
          minutesRef={startMinutesRef}
          onBlur={startTimePicker.handleBlur}
          onChange={startTimePicker.handleChange}
          styles={{ input: { textAlign: "center" } }}
          value={form.values.deliveryTargetTimeStart?.toString() ?? ""}
        />
      </Grid.Col>
      <Grid.Col span={6}>
        <TimePicker
          {...form.getInputProps(`${nameOf<OrderCreateDto>().deliveryTargetTimeEnd}`)}
          clearable
          data-testid="order-form-delivery-time-end"
          disabled={!form.values.deliveryTargetTimeStart}
          hoursRef={endHoursRef}
          label="Bis"
          min={form.values.deliveryTargetTimeStart?.toString()}
          minutesRef={endMinutesRef}
          onBlur={endTimePicker.handleBlur}
          onChange={endTimePicker.handleChange}
          styles={{ input: { textAlign: "center" } }}
          value={form.values.deliveryTargetTimeEnd?.toString() ?? ""}
        />
      </Grid.Col>
    </Grid>
  );
}
